import { useEffect, useState } from 'react';
import styles from './FixcenterFooter.module.scss';
import { useTranslation } from '../../../i18n/useTranslation';
import parse, { domToReact } from 'html-react-parser';
import { getWebshopFooter } from '../../../containers/cms-container/services';
import { Category } from '../../../components/products-page/components/products-categories/models/Category.model';
import * as authService from '../../../auth/services/auth.service';
import { User } from '../../../components/client-panel/components/my-account-info/models/user.model';
import { getUriToCalaculationProgram } from '../../../services/calculation-program.service';
import { USER_PANEL_PATHS } from '../../../shared/constants/breadcrumb.constants';
import { UserDetailsUseBetween } from '../../../shared/use-between/user-details-use-between';
import { useBetween } from '../../../shared/use-between/use-between';
import { getFixcenterCategories } from '../../../services/products.service';
import { ProductFilter } from '../../../components/product-filters-menu/models/product-filter.model';
import { FixcenterFilterDto } from '../../../components/product-filters-menu/models/fixcenter-filter-dto.model';
import FixcenterFooterSkeleton from './fixcenter-footer-skeleton';
import React from 'react';

const getFooter = (languageCode: string): any => getWebshopFooter(languageCode);

export default function FixcenterFooter() {
    const [mainCategories, setMainCategories] = useState<Category[]>([]);
    const { t } = useTranslation('translation');
    const [footer, setFooter] = useState<string>();
    const getLanguageCode = (): string | null => localStorage.getItem('lang');
    const guidEmpty = '00000000-0000-0000-0000-000000000000';
    const { userDetails } = useBetween(UserDetailsUseBetween);
    const [isCategorySkeletonLoader, setIsCategorySkeletonLoader] = useState<boolean>(false);
    const [isCmsSkeletonLoader, setIsCmsSkeletonLoader] = useState<boolean>(false);

    const fetchCategories = async () => {
        setIsCategorySkeletonLoader(true);
        const languageCode = localStorage.getItem('lang');
        const productFilter: ProductFilter = {
            isCategory: true,
            parentProductId: guidEmpty,
        };

        const fixcenterFilter: FixcenterFilterDto = {
            productFilter: productFilter,
        };

        if (languageCode) {
            fixcenterFilter.languageCode = languageCode;
        }

        try {
            getFixcenterCategories(fixcenterFilter).then((response: any) => {
                if (response) {
                    const categoriesResponse = response.data as Category[];
                    setMainCategories(categoriesResponse);
                    setIsCategorySkeletonLoader(false);
                }
            });
        } catch (error) {
            setIsCategorySkeletonLoader(false);
        }
    };

    const handleSetFooter = () => {
        setIsCmsSkeletonLoader(true);
        const languageCode = getLanguageCode();
        if (languageCode) {
            getFooter(languageCode).then((response: any) => {
                setFooter(response.data);
                setIsCmsSkeletonLoader(false);
            });
        }
    };

    const updateUrlsForCalculationProgram = (content: string): string => {
        if (!authService.checkLogInStatus()) {
            if (content.includes(USER_PANEL_PATHS[2].link)) {
                content = content.replace(USER_PANEL_PATHS[2].link, 'Login');
            }
            if (content.includes(USER_PANEL_PATHS[3].link)) {
                content = content.replace(USER_PANEL_PATHS[3].link, 'Login');
            }
        } else {
            let publicUrl = process.env.REACT_APP_WEB_SHOP_LOGIN_PAGE as string;
            publicUrl = publicUrl.replace('login', '');
            publicUrl += `${localStorage.getItem('lang')}/`;
            const companyDataUrl = `${publicUrl}${USER_PANEL_PATHS[2].link}`;
            const personalDataUrl = `${publicUrl}${USER_PANEL_PATHS[3].link}`;
            if (content.includes(companyDataUrl)) {
                content = content.replace(
                    companyDataUrl,
                    getUriToCalaculationProgram(`${USER_PANEL_PATHS[1].link}/CompanyData`, userDetails as User)
                );
            }
            if (content.includes(personalDataUrl)) {
                content = content.replace(
                    personalDataUrl,
                    getUriToCalaculationProgram(`${USER_PANEL_PATHS[1].link}/PersonalData`, userDetails as User)
                );
            }
        }

        return content;
    };

    const parseHtml = (content: string): ReturnType<typeof domToReact> => {
        content = updateUrlsForCalculationProgram(content);
        return parse(content);
    };

    useEffect(() => {
        fetchCategories();
        handleSetFooter();
    }, []);

    const renderSections = () => {
        if (!footer) return null;

        const sections = footer.split('<h3>');

        return sections.map((section, index) => {
            if (index === 0) return null;
            const [title, content] = section.split('</h3>');
            return (
                <div key={index} className={styles.footer_section}>
                    <h3>{title}</h3>
                    {parseHtml(content)}
                </div>
            );
        });
    };

    return (
        <>
            <div className={styles.container}>
                <div className={styles.footer_container}>
                    <div className={styles.columns}>
                        <div className={styles.footer_section}>
                            {isCategorySkeletonLoader ? (
                                <FixcenterFooterSkeleton></FixcenterFooterSkeleton>
                            ) : (
                                <React.Fragment>
                                    <h3>{t('fixcenterFooter.categories')}</h3>
                                    <ul>
                                        {mainCategories.map((category, index) => (
                                            <li key={index}>
                                                <a
                                                    href={
                                                        authService.checkLogInStatus() === true
                                                            ? `/webshop/category/${category.name}/${category.id}`
                                                            : '/login'
                                                    }
                                                >
                                                    {category.translation ?? category.name}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </React.Fragment>
                            )}
                        </div>
                        <React.Fragment>
                            {isCmsSkeletonLoader ? (
                                <React.Fragment>
                                    <div className={styles.footer_section}>
                                        <FixcenterFooterSkeleton></FixcenterFooterSkeleton>
                                    </div>
                                    <div className={styles.footer_section}>
                                        <FixcenterFooterSkeleton></FixcenterFooterSkeleton>
                                    </div>
                                    <div className={styles.footer_section}>
                                        <FixcenterFooterSkeleton></FixcenterFooterSkeleton>
                                    </div>
                                </React.Fragment>
                            ) : (
                                <React.Fragment>{renderSections()}</React.Fragment>
                            )}
                        </React.Fragment>
                    </div>
                </div>
            </div>
        </>
    );
}
